import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Eye, EyeOff } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/hooks/useToast';
import { getErrorMessage } from '@/services/apiClient';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import {
  getGoogleCalendarClientSettings,
  updateGoogleCalendarClientSettings,
} from '@/services/googleCalendarSettings.service';

/**
 * Locked (plan 2026-10-10-001 U7: R8, R18): the stored client shows, the
 * secret still masked, with no Add/Update Credentials and no editing hints;
 * nothing says why.
 */
export function GoogleCalendarSettingsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'google-calendar-settings'],
    queryFn: getGoogleCalendarClientSettings,
    staleTime: 30_000,
  });

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [editing, setEditing] = useState(false);

  const saveMutation = useMutation({
    mutationFn: updateGoogleCalendarClientSettings,
    onSuccess: (res) => {
      toast.success('Google Calendar credentials saved. Staff must reconnect their calendar.');
      qc.setQueryData(['admin', 'google-calendar-settings'], res);
      setEditing(false);
      setClientId('');
      setClientSecret('');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to save credentials.')),
  });

  function handleSave() {
    if (locked) return;
    const payload: Record<string, string> = {};
    if (clientId) payload.clientId = clientId;
    if (clientSecret) payload.clientSecret = clientSecret;

    if (!Object.keys(payload).length) {
      toast.error('Please fill in at least one field.');
      return;
    }
    saveMutation.mutate(payload);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <PageHeader title="Google Calendar" />

      <Card>
        <CardBody>
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="h-5 w-5 text-blue-500" />
            <h3 className="text-base font-semibold text-slate-200">Google OAuth Client</h3>
            {data?.configured ? (
              <Badge variant="success">Configured</Badge>
            ) : (
              <Badge variant="neutral">Using platform defaults</Badge>
            )}
          </div>

          <p className="text-sm text-slate-400 mb-3">
            Provide your practice's own Google OAuth client so staff connect through your verified
            Google app (no "unverified app" warning). When set, calendar sync for this account uses
            these credentials instead of the shared platform client.
            {!locked && !data?.configured && ' Leave blank to use the platform default.'}
            {!locked && data?.configured && ' Once configured, credentials can be updated but not removed.'}
          </p>

          <div className="rounded-md border border-slate-700 bg-slate-800/40 px-3 py-2 mb-5 text-xs text-slate-400 space-y-1">
            <p>
              On your Google OAuth client, add this Authorized redirect URI:
              <span className="text-slate-200 font-mono break-all"> https://api.prospera.ai/api/v5/settings/google-calendar/callback</span>
            </p>
            {!locked && (
              <p>
                After saving new credentials, staff must reconnect in <span className="text-slate-200">Settings → Calendar Sync</span> (existing
                connections were issued by the previous client).
              </p>
            )}
          </div>

          {(locked || !editing) && data?.configured && (
            <div className="space-y-3 mb-5">
              <div>
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Client ID</span>
                <p className="text-sm text-slate-200 font-mono mt-0.5 break-all">{data.credentials.clientId}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Client Secret</span>
                <p className="text-sm text-slate-200 font-mono mt-0.5">{data.credentials.clientSecret}</p>
              </div>
            </div>
          )}

          {/* Locked: no Add/Update, so the form never opens. */}
          {locked ? null : editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                  Client ID
                </label>
                <Input
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder={data?.credentials.clientId || '...apps.googleusercontent.com'}
                  className="font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                  Client Secret
                </label>
                <div className="relative">
                  <Input
                    type={showSecret ? 'text' : 'password'}
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder={data?.configured ? 'Leave blank to keep current' : 'Enter client secret'}
                    className="font-mono pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleSave} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving...' : 'Save Credentials'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEditing(false);
                    setClientId('');
                    setClientSecret('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button onClick={() => setEditing(true)}>
                {data?.configured ? 'Update Credentials' : 'Add Credentials'}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
